import { isApiError } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { EmptyState, ErrorState, Skeleton } from "@sagot/ui";
import { useParams } from "react-router";
import { UpgradeCard, isPlanGateError } from "../shared/plan-gate";
import { useAgent } from "../shared/use-shared-queries";
import { FLUSH } from "./layout";
import { useWidget } from "./use-widget-studio";
import { WidgetEditor } from "./widget-editor";

const NOT_FOUND = 404;

function StudioSkeleton() {
  return (
    <div className={`${FLUSH} flex flex-col lg:h-[calc(100vh-3.5rem)] lg:flex-row`} aria-hidden="true" data-testid="widget-studio-skeleton">
      <div className="space-y-5 border-b border-line bg-surface p-5 lg:w-[420px] lg:shrink-0 lg:border-b-0 lg:border-r">
        <Skeleton className="h-8 w-[200px]" />
        <Skeleton className="h-4 w-[260px]" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
      <div className="flex-1 bg-canvas p-4 lg:p-8">
        <Skeleton className="mx-auto h-[640px] max-w-5xl" />
      </div>
    </div>
  );
}

/** `/w/:ws/agents/:agentId/widget/:tab?` — Widget Studio (tenant/widget-studio.html). */
export function WidgetStudioPage() {
  const { t } = useTranslation();
  const agentId = useParams().agentId ?? "";
  const widget = useWidget(agentId);
  const agent = useAgent(agentId);
  if (widget.isPending) return <StudioSkeleton />;
  if (widget.isError) {
    if (isPlanGateError(widget.error)) return <UpgradeCard feature={t("deployKnowledge.widgetStudio.title")} />;
    if (isApiError(widget.error) && widget.error.status === NOT_FOUND) {
      return (
        <div className="card mx-auto max-w-3xl">
          <h1 className="sr-only">{t("deployKnowledge.widgetStudio.title")}</h1>
          <EmptyState icon="bot" title={t("deployKnowledge.widgetStudio.notFoundTitle")} description={t("deployKnowledge.widgetStudio.notFoundBody")} />
        </div>
      );
    }
    return (
      <div className="card mx-auto max-w-3xl">
        <h1 className="sr-only">{t("deployKnowledge.widgetStudio.title")}</h1>
        <ErrorState title={t("deployKnowledge.loadError")} description={t("deployKnowledge.loadErrorBody")} onRetry={() => void widget.refetch()} retryLabel={t("common.retry")} />
      </div>
    );
  }
  return <WidgetEditor key={agentId} agentId={agentId} agentName={agent.data?.name ?? ""} agentLanguages={agent.data?.languages ?? []} agentStatus={agent.data?.status} widget={widget.data} />;
}
